import { useState, type ReactNode } from 'react';
import { XIcon } from '@phosphor-icons/react';
import { CategoryTile } from '@/components/CategoryTile';
import { getCategoryMeta, type CategoryIndex } from '@/components/transactions/screen/categoryIndex';
import {
  TransactionAmount,
  TransactionCategoryLabel,
  TransactionLine,
  TransactionMeta,
  TransactionMetaItem,
  TransactionTitle,
} from '@/components/transactions/TransactionLine';
import { Button } from '@/components/ui/button';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer } from '@/components/ui/responsive-drawer';
import { Skeleton } from '@/components/ui/skeleton';
import { useCategoryIndex } from '@/hooks/useCategoryIndex';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useSpaceExpensesPage } from '@/hooks/queries/useSpaceExpensesPage';
import { expenseDate, type SpaceExpenseRow } from '@/lib/spaces/expenses';
import { isOverview, readFailed } from '@/lib/spaces/home';
import { memberPhoto, participantName, settlementBalanceDisplay, settlementPeriodLabel } from '@/lib/spaces/settlement';
import type { SpaceDetail } from '@/types/spaces';
import type { SpaceLedger, SpaceLedgerMember } from '@/types/space-settlements';
import { SettlementAvatar, SettlementFailure, SettlementMoney, SettlementPagination } from './SettlementParts';

const PAGE_SIZE = 10;
const EYEBROW = 'font-mono text-2xs font-semibold uppercase tracking-widest text-text-secondary';

// Opened over the Acerto sheet, which stays mounted underneath: the transfers ticked there survive a look at who paid
// what. The expenses read the ledger's own bounds, so the running balance lists everything since the space began.
export function SpacePersonExpensesDrawer({ space, ledger, member, close }: Readonly<{
  space: SpaceDetail; ledger: SpaceLedger; member: SpaceLedgerMember; close: () => void;
}>) {
  const name = participantName(space, member.userId);
  const handleOpenChange = (open: boolean) => { if (!open) close(); };
  return <ResponsiveDrawer open onOpenChange={handleOpenChange}>
    <DrawerContent className="overflow-clip bg-surface p-0 data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:max-w-lg">
      <DrawerHeader className="shrink-0 border-b border-border-subtle px-5 py-4 text-left group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left">
        <div className="flex items-center justify-between gap-3">
          <DrawerTitle className="flex min-w-0 items-center gap-2"><SettlementAvatar name={name} photoUrl={memberPhoto(space, member.userId)} className="size-7 text-2xs" /><span className="min-w-0 break-words">{name}</span></DrawerTitle>
          <Button variant="ghost" size="icon" aria-label={`Fechar as despesas de ${name}`} onClick={close}><XIcon aria-hidden className="size-4" /></Button>
        </div>
        <DrawerDescription className="break-words text-text-secondary">{space.name} · {settlementPeriodLabel(ledger.periodKey)}</DrawerDescription>
      </DrawerHeader>
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-4" data-vaul-no-drag>
        <BalanceSum member={member} />
        <PaidExpenses space={space} ledger={ledger} member={member} name={name} />
      </div>
    </DrawerContent>
  </ResponsiveDrawer>;
}

// The balance as the sum it is: what they paid, minus their part, plus what they sent, minus what they received.
function BalanceSum({ member }: Readonly<{ member: SpaceLedgerMember }>) {
  const balance = settlementBalanceDisplay(member.balanceCents);
  return <section aria-label="Como o saldo se forma" className="space-y-2">
    <h3 className={EYEBROW}>Como o saldo se forma</h3>
    <dl className="space-y-1.5 rounded-xl bg-surface-sunken p-4 text-sm">
      <SumLine label="Pagou" sign="" cents={member.paidCents} />
      <SumLine label="Parte nas despesas" sign="−" cents={member.owedCents} />
      <SumLine label="Já transferiu" sign="+" cents={member.sentCents} visible={member.sentCents > 0} />
      <SumLine label="Já recebeu" sign="−" cents={member.receivedCents} visible={member.receivedCents > 0} />
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 border-t border-border-subtle pt-2">
        <dt className="font-semibold text-text-primary">{balance.label}</dt>
        <dd><SettlementMoney cents={member.balanceCents} signed className={`font-semibold ${balance.color}`} /></dd>
      </div>
    </dl>
  </section>;
}

function SumLine({ label, sign, cents, visible = true }: Readonly<{ label: string; sign: string; cents: number; visible?: boolean }>) {
  if (!visible) return null;
  return <div className="flex flex-wrap items-baseline justify-between gap-x-3">
    <dt className="text-text-secondary"><span className="inline-block w-3 font-mono">{sign}</span>{label}</dt>
    <dd><SettlementMoney cents={cents} className="text-text-primary" /></dd>
  </div>;
}

function PaidExpenses({ space, ledger, member, name }: Readonly<{ space: SpaceDetail; ledger: SpaceLedger; member: SpaceLedgerMember; name: string }>) {
  const [offset, setOffset] = useState(0);
  const page = useSpaceExpensesPage(space.id, ledger.periodKey, `payer:${member.userId}`, offset, PAGE_SIZE);
  const categoryIndex = useCategoryIndex();
  if (readFailed(page, isOverview)) return <PaidSection><SettlementFailure message="Não foi possível carregar as despesas." retry={page.mutate} /></PaidSection>;
  if (!page.data) return <PaidSection><div aria-busy="true" aria-label={`Carregando as despesas de ${name}`} className="space-y-2"><Skeleton className="h-12 rounded-xl" /><Skeleton className="h-12 rounded-xl" /></div></PaidSection>;
  if (!page.data.expenses.total) return <PaidSection><p className="text-sm text-text-secondary">Nenhuma despesa paga ainda.</p></PaidSection>;
  const renderExpense = (expense: SpaceExpenseRow) => <PaidExpense key={expense.id} expense={expense} categoryIndex={categoryIndex} />;
  return <PaidSection total={<PaidTotal count={page.data.expenses.total} cents={page.data.sharedAmountCents} />}>
    <ul aria-label={`Despesas pagas por ${name}`} className="divide-y divide-border-subtle">{page.data.expenses.items.map(renderExpense)}</ul>
    <SettlementPagination offset={offset} total={page.data.expenses.total} limit={PAGE_SIZE} onPage={setOffset} />
  </PaidSection>;
}

function PaidSection({ total, children }: Readonly<{ total?: ReactNode; children: ReactNode }>) {
  return <section aria-label="Despesas pagas" className="space-y-2">
    <div className="flex flex-wrap items-baseline justify-between gap-x-3"><h3 className={EYEBROW}>Despesas pagas</h3>{total}</div>
    {children}
  </section>;
}

function PaidTotal({ count, cents }: Readonly<{ count: number; cents: number }>) {
  const format = useFormatCurrency();
  return <span className="text-xs text-text-secondary">{count.toLocaleString('pt-BR')} {count === 1 ? 'despesa' : 'despesas'} · <span className="font-semibold text-text-primary">{format(cents / 100)}</span></span>;
}

// A fatura item's compact line: the tile, the name, the category and the day, the shared amount.
function PaidExpense({ expense, categoryIndex }: Readonly<{ expense: SpaceExpenseRow; categoryIndex: CategoryIndex }>) {
  const category = getCategoryMeta(categoryIndex, expense.categoryId);
  return <TransactionLine as="li" density="compact" leading={<CategoryTile meta={category} size="sm" />}
    title={<TransactionTitle>{expense.name}</TransactionTitle>}
    meta={<TransactionMeta>
      <TransactionMetaItem><TransactionCategoryLabel meta={category} /></TransactionMetaItem>
      <TransactionMetaItem separated><span className="whitespace-nowrap">{expenseDate(expense.occurredAt)}</span></TransactionMetaItem>
    </TransactionMeta>}
    amount={<TransactionAmount value={expense.sharedAmountCents / 100} tone="neutral" />} />;
}
